import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RealtimeProvider } from './contexts/RealtimeContext';

// Components
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import DiagnosticPage from './components/Auth/DiagnosticPage';
import HomePage from './components/HomePage/HomePage';
import FighterProfile from './components/FighterProfile/FighterProfile';
import FighterMedia from './components/FighterMedia/FighterMedia';
import Rankings from './components/Rankings/Rankings';
import Matchmaking from './components/Matchmaking/Matchmaking';
import Scheduling from './components/Scheduling/Scheduling';
import Tournaments from './components/Tournaments/Tournaments';
import Analytics from './components/Analytics/Analytics';
import AdminPanel from './components/Admin/AdminPanel';
import Navigation from './components/Shared/Navigation';
import LoadingSpinner from './components/Shared/LoadingSpinner';
import Social from './components/Social/Social';
import RulesGuidelines from './components/RulesGuidelines/RulesGuidelines';

// Debug: Verify RulesGuidelines is imported
if (process.env.NODE_ENV === 'development') {
  console.log('RulesGuidelines component imported:', typeof RulesGuidelines);
}

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#d32f2f', // Boxing red
      light: '#ff6659',
      dark: '#9a0007',
    },
    secondary: {
      main: '#ff9800', // Gold
      light: '#ffb74d',
      dark: '#f57c00',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Main App Content
const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Check if we're on the public media page
  const isPublicMediaPage = location.pathname.startsWith('/media/');

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Hide navigation on public media pages */}
      {user && !isPublicMediaPage && <Navigation />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isPublicMediaPage ? 0 : 3, // Remove padding on media page
          width: { sm: isPublicMediaPage ? '100%' : `calc(100% - 200px)` },
          ml: { sm: isPublicMediaPage ? 0 : '200px' },
        }}
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
          <Route path="/diagnostic" element={<DiagnosticPage />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <FighterProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fighter/:userId"
            element={
              <ProtectedRoute>
                <FighterProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/media/:userId"
            element={<FighterMedia />}
          />
          <Route
            path="/rankings"
            element={
              <ProtectedRoute>
                <Rankings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/matchmaking"
            element={
              <ProtectedRoute>
                <Matchmaking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduling"
            element={
              <ProtectedRoute>
                <Scheduling />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournaments"
            element={
              <ProtectedRoute>
                <Tournaments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social"
            element={
              <ProtectedRoute>
                <Social />
              </ProtectedRoute>
            }
          />
          {/* Rules/Guidelines route - must be before catch-all route */}
          <Route
            path="/rules"
            element={
              <ProtectedRoute>
                <RulesGuidelines />
              </ProtectedRoute>
            }
          />
          {/* Explicit route for /rules/ to handle trailing slash */}
          <Route
            path="/rules/"
            element={
              <ProtectedRoute>
                <RulesGuidelines />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            }
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <RealtimeProvider>
          <Router>
            <AppContent />
          </Router>
        </RealtimeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;